import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { 
    Package, 
    Search,
    AlertTriangle,
    CheckCircle,
    Clock,
    Truck,
    FileText,
    Upload,
    RefreshCw,
    CreditCard,
    Banknote,
    X,
    Eye,
    Download,
    Square,
    CheckSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminSAVPage = () => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [savRequests, setSavRequests] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Detail modal
    const [selectedSAV, setSelectedSAV] = useState(null);
    const [savDetails, setSavDetails] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    
    // Actions
    const [actionLoading, setActionLoading] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    const [creditAmount, setCreditAmount] = useState('');
    
    // Item validation (for multi-item SAV)
    const [itemValidations, setItemValidations] = useState({});
    // Item discounts (décote par appareil)
    const [itemDiscounts, setItemDiscounts] = useState({});

    const fetchSAV = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/admin/sav`;
            if (statusFilter) {
                url += `?status=${statusFilter}`;
            }
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavRequests(response.data.sav_requests || []);
        } catch (error) {
            toast.error('Erreur lors du chargement des SAV');
        } finally {
            setLoading(false);
        }
    }, [token, statusFilter]);

    useEffect(() => {
        fetchSAV();
    }, [fetchSAV]);

    const fetchSAVDetails = async (savId) => {
        setDetailLoading(true);
        try {
            const response = await axios.get(`${API_URL}/admin/sav/${savId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSavDetails(response.data);
            setAdminNotes(response.data.sav?.admin_notes || '');
            setCreditAmount(response.data.sav?.total_value?.toString() || response.data.sav?.item?.price?.toString() || '');
            
            // Initialize item validations - use existing validation_status if available, otherwise default to approved
            const items = response.data.sav?.items || [];
            const validations = {};
            items.forEach(item => {
                validations[item.imei] = { 
                    status: item.validation_status || 'approved', 
                    reason: item.rejection_reason || '' 
                };
            });
            // Handle legacy single item
            if (items.length === 0 && response.data.sav?.imei) {
                validations[response.data.sav.imei] = { status: 'approved', reason: '' };
            }
            setItemValidations(validations);
        } catch (error) {
            toast.error('Erreur lors du chargement des détails');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleOpenDetails = (sav) => {
        setSelectedSAV(sav);
        setItemValidations({});
        fetchSAVDetails(sav.id);
    };

    const toggleItemValidation = (imei) => {
        setItemValidations(prev => ({
            ...prev,
            [imei]: {
                ...prev[imei],
                status: prev[imei]?.status === 'approved' ? 'rejected' : 'approved'
            }
        }));
    };

    const setItemRejectionReason = (imei, reason) => {
        setItemValidations(prev => ({
            ...prev,
            [imei]: {
                ...prev[imei],
                reason
            }
        }));
    };

    // Set discount for an item
    const setItemDiscount = (imei, discount) => {
        setItemDiscounts(prev => ({
            ...prev,
            [imei]: parseFloat(discount) || 0
        }));
    };

    // Calculate total credit based on approved items and discounts
    const calculateApprovedTotal = () => {
        if (!savDetails?.sav) return 0;
        
        const items = savDetails.sav.items || [];
        let total = 0;
        const status = savDetails.sav.status;
        
        if (items.length > 0) {
            // Multi-item SAV
            items.forEach(item => {
                let isApproved = false;
                
                if (['pending', 'processing', 'received'].includes(status)) {
                    // During editable phases, use itemValidations state
                    isApproved = itemValidations[item.imei]?.status === 'approved';
                } else {
                    // For finalized phases, use validation_status from DB
                    isApproved = item.validation_status === 'approved';
                }
                
                if (isApproved) {
                    const price = item.item?.price || 0;
                    const discount = itemDiscounts[item.imei] || 0;
                    total += Math.max(0, price - discount);
                }
            });
        } else if (savDetails.sav.imei) {
            // Legacy single item
            const validation = itemValidations[savDetails.sav.imei];
            if (validation?.status === 'approved') {
                const price = savDetails.sav.item?.price || 0;
                const discount = itemDiscounts[savDetails.sav.imei] || 0;
                total += Math.max(0, price - discount);
            }
        }
        
        return total;
    };

    // Update credit amount when validations or discounts change
    useEffect(() => {
        if (['processing', 'pending', 'received'].includes(savDetails?.sav?.status)) {
            const calculatedTotal = calculateApprovedTotal();
            if (calculatedTotal > 0) {
                setCreditAmount(calculatedTotal.toFixed(2));
            }
        }
    }, [itemValidations, itemDiscounts, savDetails]);

    const handleApproveSAV = async () => {
        if (!selectedSAV) return;
        
        // Check if client wants transport label but it's not uploaded
        const wantsLabel = savDetails?.sav?.wants_transport_label;
        const hasLabel = !!savDetails?.sav?.return_label_url;
        
        if (wantsLabel && !hasLabel) {
            toast.error('Le client a demandé un bon de transport. Veuillez l\'uploader avant de valider.');
            return;
        }
        
        // Check that all rejected items have a reason
        const rejectedWithoutReason = Object.entries(itemValidations).filter(
            ([_, val]) => val.status === 'rejected' && !val.reason?.trim()
        );
        if (rejectedWithoutReason.length > 0) {
            toast.error('Veuillez indiquer un motif pour chaque appareil refusé.');
            return;
        }
        
        setActionLoading(true);
        try {
            // No discount at this stage - discounts are applied after physical inspection
            const items = Object.entries(itemValidations).map(([imei, val]) => ({
                imei,
                status: val.status,
                reason: val.reason || null
            }));
            
            const response = await axios.post(
                `${API_URL}/admin/sav/${selectedSAV.id}/approve`,
                {
                    items,
                    admin_notes: adminNotes
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(response.data.message);
            fetchSAV();
            fetchSAVDetails(selectedSAV.id);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateStatus = async (status, refundType = null) => {
        if (!selectedSAV) return;
        
        setActionLoading(true);
        try {
            // Build items validation data to send to backend
            const items = Object.entries(itemValidations).map(([imei, val]) => ({
                imei,
                validation_status: val.status,
                rejection_reason: val.reason || null,
                discount: itemDiscounts[imei] || 0
            }));
            
            await axios.put(
                `${API_URL}/admin/sav/${selectedSAV.id}/status`,
                {
                    status,
                    admin_notes: adminNotes,
                    refund_type: refundType,
                    credit_amount: status === 'refunded_credit' ? parseFloat(creditAmount) : null,
                    items: items  // Send updated item validations
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Statut mis à jour');
            fetchSAV();
            fetchSAVDetails(selectedSAV.id);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUploadLabel = async () => {
        if (!selectedSAV || !uploadFile) return;
        
        setActionLoading(true);
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const base64 = e.target.result.split(',')[1];
                await axios.post(
                    `${API_URL}/admin/sav/${selectedSAV.id}/upload-return-label`,
                    {
                        file_data: base64,
                        filename: uploadFile.name
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Bon de transport uploadé');
                setUploadFile(null);
                fetchSAV();
                fetchSAVDetails(selectedSAV.id);
                setActionLoading(false);
            };
            reader.readAsDataURL(uploadFile);
        } catch (error) {
            toast.error('Erreur lors de l\'upload');
            setActionLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'pending': { color: 'bg-amber-100 text-amber-800', label: 'En attente' },
            'approved': { color: 'bg-blue-100 text-blue-800', label: 'Approuvé' },
            'shipped_back': { color: 'bg-indigo-100 text-indigo-800', label: 'Retour expédié' },
            'received': { color: 'bg-purple-100 text-purple-800', label: 'Reçu' },
            'processing': { color: 'bg-orange-100 text-orange-800', label: 'En examen' },
            'refunded_credit': { color: 'bg-green-100 text-green-800', label: 'Crédit accordé' },
            'refunded_transfer': { color: 'bg-emerald-100 text-emerald-800', label: 'Remboursé virement' },
            'rejected': { color: 'bg-red-100 text-red-800', label: 'Refusé' }
        };
        const config = statusConfig[status] || { color: 'bg-gray-100', label: status };
        return <Badge className={config.color}>{config.label}</Badge>;
    };

    const filteredSAV = savRequests.filter(sav => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            sav.id.toLowerCase().includes(q) ||
            sav.imei.includes(q) ||
            sav.user_email?.toLowerCase().includes(q) ||
            sav.item?.brand?.toLowerCase().includes(q) ||
            sav.item?.model?.toLowerCase().includes(q)
        );
    });

    const stats = {
        pending: savRequests.filter(s => s.status === 'pending').length,
        approved: savRequests.filter(s => s.status === 'approved').length,
        shipped_back: savRequests.filter(s => s.status === 'shipped_back').length,
        processing: savRequests.filter(s => ['received', 'processing'].includes(s.status)).length,
        completed: savRequests.filter(s => ['refunded_credit', 'refunded_transfer', 'rejected'].includes(s.status)).length
    };

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">
                            Gestion SAV
                        </h1>
                        <p className="text-slate-600">
                            {savRequests.length} demandes au total
                        </p>
                    </div>
                    <Button onClick={fetchSAV} variant="outline">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-5 gap-4">
                    <Card className="cursor-pointer hover:bg-amber-50" onClick={() => setStatusFilter('pending')}>
                        <CardContent className="py-4 text-center">
                            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                            <p className="text-sm text-slate-600">En attente</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:bg-blue-50" onClick={() => setStatusFilter('approved')}>
                        <CardContent className="py-4 text-center">
                            <p className="text-2xl font-bold text-blue-600">{stats.approved}</p>
                            <p className="text-sm text-slate-600">Approuvés</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:bg-indigo-50" onClick={() => setStatusFilter('shipped_back')}>
                        <CardContent className="py-4 text-center">
                            <p className="text-2xl font-bold text-indigo-600">{stats.shipped_back}</p>
                            <p className="text-sm text-slate-600">Retours expédiés</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:bg-purple-50" onClick={() => setStatusFilter('received,processing')}>
                        <CardContent className="py-4 text-center">
                            <p className="text-2xl font-bold text-purple-600">{stats.processing}</p>
                            <p className="text-sm text-slate-600">En traitement</p>
                        </CardContent>
                    </Card>
                    <Card className="cursor-pointer hover:bg-green-50" onClick={() => setStatusFilter('refunded_credit,refunded_transfer,rejected')}>
                        <CardContent className="py-4 text-center">
                            <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                            <p className="text-sm text-slate-600">Terminés</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <div className="flex gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Rechercher par IMEI, email, modèle..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    {statusFilter && (
                        <Button variant="outline" onClick={() => setStatusFilter('')}>
                            <X className="w-4 h-4 mr-1" />
                            Réinitialiser filtre
                        </Button>
                    )}
                </div>

                {/* Table */}
                <Card>
                    <CardContent className="p-0">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b">
                                <tr>
                                    <th className="text-left p-3 text-xs font-medium text-slate-500">Ref</th>
                                    <th className="text-left p-3 text-xs font-medium text-slate-500">Client</th>
                                    <th className="text-left p-3 text-xs font-medium text-slate-500">Appareil</th>
                                    <th className="text-left p-3 text-xs font-medium text-slate-500">IMEI</th>
                                    <th className="text-left p-3 text-xs font-medium text-slate-500">Éligible</th>
                                    <th className="text-left p-3 text-xs font-medium text-slate-500">Statut</th>
                                    <th className="text-left p-3 text-xs font-medium text-slate-500">Date</th>
                                    <th className="text-right p-3 text-xs font-medium text-slate-500">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={8} className="text-center py-8">
                                            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                                        </td>
                                    </tr>
                                ) : filteredSAV.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center py-8 text-slate-500">
                                            Aucune demande SAV
                                        </td>
                                    </tr>
                                ) : (
                                    filteredSAV.map((sav) => (
                                        <tr key={sav.id} className="border-b hover:bg-slate-50">
                                            <td className="p-3 font-mono text-sm">#{sav.id.slice(0, 8)}</td>
                                            <td className="p-3 text-sm">{sav.user_email}</td>
                                            <td className="p-3">
                                                <p className="font-medium text-sm">{sav.item?.brand} {sav.item?.model}</p>
                                                <p className="text-xs text-slate-500">{sav.item?.storage_capacity}GB</p>
                                            </td>
                                            <td className="p-3 font-mono text-sm">{sav.imei}</td>
                                            <td className="p-3">
                                                {sav.is_eligible ? (
                                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                                ) : (
                                                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                                                )}
                                            </td>
                                            <td className="p-3">{getStatusBadge(sav.status)}</td>
                                            <td className="p-3 text-sm text-slate-500">
                                                {new Date(sav.created_at).toLocaleDateString('fr-FR')}
                                            </td>
                                            <td className="p-3 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {/* Download credit note if available */}
                                                    {(sav.status === 'refunded_credit' || sav.status === 'refunded_transfer') && sav.credit_note_url && (
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm"
                                                            onClick={() => window.open(sav.credit_note_url, '_blank')}
                                                            title="Télécharger l'avoir"
                                                        >
                                                            <Download className="w-4 h-4 text-green-600" />
                                                        </Button>
                                                    )}
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={() => handleOpenDetails(sav)}
                                                        title="Voir détails"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>

                {/* Detail Modal */}
                <Dialog open={!!selectedSAV} onOpenChange={() => setSelectedSAV(null)}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                Demande SAV #{selectedSAV?.id.slice(0, 8)}
                            </DialogTitle>
                        </DialogHeader>

                        {detailLoading ? (
                            <div className="py-8 text-center">
                                <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                            </div>
                        ) : savDetails && (
                            <div className="space-y-6">
                                {/* Status */}
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    {getStatusBadge(savDetails.sav.status)}
                                    <div className="flex gap-2">
                                        {!savDetails.sav.is_eligible && (
                                            <Badge className="bg-amber-100 text-amber-800">
                                                <AlertTriangle className="w-3 h-3 mr-1" />
                                                Hors garantie
                                            </Badge>
                                        )}
                                        {savDetails.sav.wants_transport_label && (
                                            <Badge className={savDetails.sav.return_label_url ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}>
                                                <Truck className="w-3 h-3 mr-1" />
                                                {savDetails.sav.return_label_url ? "Bon transport uploadé" : "Bon transport demandé"}
                                            </Badge>
                                        )}
                                    </div>
                                </div>

                                {/* Client Info */}
                                <Card>
                                    <CardHeader className="py-3">
                                        <CardTitle className="text-sm">Client</CardTitle>
                                    </CardHeader>
                                    <CardContent className="py-2">
                                        <p className="font-medium">{savDetails.user?.company_name}</p>
                                        <p className="text-sm text-slate-600">{savDetails.user?.email}</p>
                                    </CardContent>
                                </Card>

                                {/* Item Info */}
                                <Card>
                                    <CardHeader className="py-3">
                                        <CardTitle className="text-sm">
                                            Appareil{savDetails.sav.items?.length > 1 ? 's concernés' : ' concerné'}
                                            {['pending', 'processing', 'received'].includes(savDetails.sav.status) && (
                                                <span className="text-xs font-normal text-slate-500 ml-2">
                                                    (Utilisez les boutons pour accepter/refuser)
                                                </span>
                                            )}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="py-2">
                                        {savDetails.sav.items && savDetails.sav.items.length > 0 ? (
                                            <div className="space-y-3">
                                                {savDetails.sav.items.map((item, idx) => {
                                                    const validation = itemValidations[item.imei];
                                                    const isApproved = validation?.status === 'approved';
                                                    // Allow editing when status is pending, processing or received
                                                    const canEdit = ['pending', 'processing', 'received'].includes(savDetails.sav.status);
                                                    
                                                    return (
                                                        <div 
                                                            key={idx} 
                                                            className={`p-3 rounded border transition-colors ${
                                                                canEdit 
                                                                    ? isApproved 
                                                                        ? 'bg-green-50 border-green-200' 
                                                                        : 'bg-red-50 border-red-200'
                                                                    : item.validation_status === 'approved'
                                                                        ? 'bg-green-50 border-green-200'
                                                                        : item.validation_status === 'rejected'
                                                                            ? 'bg-red-50 border-red-200'
                                                                            : 'bg-slate-50'
                                                            }`}
                                                        >
                                                            <div className="flex items-start justify-between">
                                                                <div className="flex-1">
                                                                    <p className="font-medium">
                                                                        {item.item?.brand} {item.item?.model}
                                                                    </p>
                                                                    <p className="text-sm text-slate-600">
                                                                        {item.item?.storage_capacity}GB • Grade {item.item?.grade}
                                                                    </p>
                                                                    <p className="font-mono text-sm">IMEI: {item.imei}</p>
                                                                    <p className="font-bold mt-1">{item.item?.price?.toFixed(2)} € TTC</p>
                                                                    {item.reason && (
                                                                        <p className="text-sm text-slate-600 mt-2 italic border-t pt-2">
                                                                            Raison client: "{item.reason}"
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                {canEdit && (
                                                                    <div className="flex flex-col gap-2">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleItemValidation(item.imei)}
                                                                            className={`flex items-center gap-2 px-3 py-2 rounded-md font-medium transition-all ${
                                                                                isApproved 
                                                                                    ? 'bg-green-600 text-white hover:bg-green-700' 
                                                                                    : 'bg-white border-2 border-green-300 text-green-700 hover:bg-green-50'
                                                                            }`}
                                                                        >
                                                                            {isApproved ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                                                            Accepter
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleItemValidation(item.imei)}
                                                                            className={`flex items-center gap-2 px-3 py-2 rounded-md font-medium transition-all ${
                                                                                !isApproved 
                                                                                    ? 'bg-red-600 text-white hover:bg-red-700' 
                                                                                    : 'bg-white border-2 border-red-300 text-red-700 hover:bg-red-50'
                                                                            }`}
                                                                        >
                                                                            {!isApproved ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                                                            Refuser
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                {!canEdit && item.validation_status && (
                                                                    <Badge className={item.validation_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                                                        {item.validation_status === 'approved' ? 'Accepté' : 'Refusé'}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            {canEdit && !isApproved && (
                                                                <div className="mt-2">
                                                                    <Input
                                                                        placeholder="Motif du refus (obligatoire)..."
                                                                        value={validation?.reason || ''}
                                                                        onChange={(e) => setItemRejectionReason(item.imei, e.target.value)}
                                                                        className="text-sm border-red-300"
                                                                    />
                                                                </div>
                                                            )}
                                                            {/* No discount at approval stage - discounts applied after physical inspection */}
                                                        </div>
                                                    );
                                                })}
                                                <div className="border-t pt-2 font-bold flex justify-between">
                                                    <span>Total:</span>
                                                    <span>{savDetails.sav.total_value?.toFixed(2)} € TTC</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div 
                                                className={`p-3 rounded border ${
                                                    ['pending', 'processing', 'received'].includes(savDetails.sav.status)
                                                        ? itemValidations[savDetails.sav.imei]?.status === 'approved'
                                                            ? 'bg-green-50 border-green-200'
                                                            : 'bg-red-50 border-red-200'
                                                        : 'bg-slate-50'
                                                }`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="font-medium">
                                                            {savDetails.sav.item?.brand} {savDetails.sav.item?.model}
                                                        </p>
                                                        <p className="text-sm text-slate-600">
                                                            {savDetails.sav.item?.storage_capacity}GB • Grade {savDetails.sav.item?.grade}
                                                        </p>
                                                        <p className="font-mono text-sm">IMEI: {savDetails.sav.imei}</p>
                                                        <p className="font-bold mt-2">{savDetails.sav.item?.price?.toFixed(2)} € TTC</p>
                                                        {savDetails.sav.reason && (
                                                            <p className="text-sm text-slate-600 mt-2 italic border-t pt-2">
                                                                Raison: "{savDetails.sav.reason}"
                                                            </p>
                                                        )}
                                                    </div>
                                                    {['pending', 'processing', 'received'].includes(savDetails.sav.status) && (
                                                        <div className="flex flex-col gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleItemValidation(savDetails.sav.imei)}
                                                                className={`flex items-center gap-2 px-3 py-2 rounded-md font-medium transition-all ${
                                                                    itemValidations[savDetails.sav.imei]?.status === 'approved'
                                                                        ? 'bg-green-600 text-white hover:bg-green-700' 
                                                                        : 'bg-white border-2 border-green-300 text-green-700 hover:bg-green-50'
                                                                }`}
                                                            >
                                                                {itemValidations[savDetails.sav.imei]?.status === 'approved' ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                                                Accepter
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleItemValidation(savDetails.sav.imei)}
                                                                className={`flex items-center gap-2 px-3 py-2 rounded-md font-medium transition-all ${
                                                                    itemValidations[savDetails.sav.imei]?.status !== 'approved'
                                                                        ? 'bg-red-600 text-white hover:bg-red-700' 
                                                                        : 'bg-white border-2 border-red-300 text-red-700 hover:bg-red-50'
                                                                }`}
                                                            >
                                                                {itemValidations[savDetails.sav.imei]?.status !== 'approved' ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                                                Refuser
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {['pending', 'processing', 'received'].includes(savDetails.sav.status) && itemValidations[savDetails.sav.imei]?.status !== 'approved' && (
                                                    <div className="mt-2">
                                                        <Input
                                                            placeholder="Motif du refus (obligatoire)..."
                                                            value={itemValidations[savDetails.sav.imei]?.reason || ''}
                                                            onChange={(e) => setItemRejectionReason(savDetails.sav.imei, e.target.value)}
                                                            className="text-sm border-red-300"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Admin Notes */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Notes admin</label>
                                    <Textarea
                                        value={adminNotes}
                                        onChange={(e) => setAdminNotes(e.target.value)}
                                        placeholder="Notes internes..."
                                        rows={3}
                                    />
                                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                        <span className="text-amber-500">⚠️</span>
                                        Attention : ces notes sont visibles par le client
                                    </p>
                                </div>

                                {/* Actions based on status */}
                                <div className="space-y-4 border-t pt-4">
                                    <h4 className="font-medium">Actions</h4>

                                    {/* Pending -> Approve or Reject */}
                                    {savDetails.sav.status === 'pending' && (
                                        <div className="space-y-4">
                                            {/* Summary of validation with amounts */}
                                            <div className="p-3 bg-slate-50 rounded border">
                                                <p className="text-sm font-medium mb-1">Résumé validation:</p>
                                                <p className="text-sm text-green-600">
                                                    ✓ {Object.values(itemValidations).filter(v => v.status === 'approved').length} appareil(s) accepté(s)
                                                </p>
                                                <p className="text-sm text-red-600">
                                                    ✗ {Object.values(itemValidations).filter(v => v.status === 'rejected').length} appareil(s) refusé(s)
                                                </p>
                                                {calculateApprovedTotal() > 0 && (
                                                    <div className="mt-2 pt-2 border-t">
                                                        <p className="text-sm font-medium text-green-700">
                                                            Montant avoir: {calculateApprovedTotal().toFixed(2)} € TTC
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Transport label section - shown if client requested it */}
                                            {savDetails.sav.wants_transport_label && (
                                                <div className={`p-3 rounded border ${savDetails.sav.return_label_url ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Truck className={`w-4 h-4 ${savDetails.sav.return_label_url ? 'text-green-600' : 'text-amber-600'}`} />
                                                        <span className={`text-sm font-medium ${savDetails.sav.return_label_url ? 'text-green-800' : 'text-amber-800'}`}>
                                                            {savDetails.sav.return_label_url 
                                                                ? '✓ Bon de transport uploadé'
                                                                : '⚠️ Le client a demandé un bon de transport'
                                                            }
                                                        </span>
                                                    </div>
                                                    {!savDetails.sav.return_label_url && (
                                                        <div className="flex gap-2">
                                                            <Input
                                                                type="file"
                                                                accept=".pdf"
                                                                onChange={(e) => setUploadFile(e.target.files[0])}
                                                                className="flex-1 text-sm"
                                                            />
                                                            <Button 
                                                                size="sm"
                                                                onClick={handleUploadLabel}
                                                                disabled={!uploadFile || actionLoading}
                                                            >
                                                                <Upload className="w-4 h-4 mr-1" />
                                                                Uploader
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {/* Upload transport label (optional if client didn't request) */}
                                            {!savDetails.sav.wants_transport_label && (
                                                <div className="border-t pt-3">
                                                    <p className="text-sm text-slate-600 mb-2">
                                                        Uploader un bon de transport (optionnel) :
                                                    </p>
                                                    <div className="flex gap-2">
                                                        <Input
                                                            type="file"
                                                            accept=".pdf"
                                                            onChange={(e) => setUploadFile(e.target.files[0])}
                                                            className="flex-1 text-sm"
                                                        />
                                                        <Button 
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={handleUploadLabel}
                                                            disabled={!uploadFile || actionLoading}
                                                        >
                                                            <Upload className="w-4 h-4 mr-1" />
                                                            Uploader
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Accept button (generates SAV slip) */}
                                            <Button 
                                                onClick={handleApproveSAV}
                                                disabled={
                                                    actionLoading || 
                                                    Object.values(itemValidations).filter(v => v.status === 'approved').length === 0 ||
                                                    (savDetails.sav.wants_transport_label && !savDetails.sav.return_label_url)
                                                }
                                                className="w-full bg-green-600 hover:bg-green-700"
                                            >
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                {actionLoading ? 'Validation...' : 'Valider et envoyer la réponse au client'}
                                            </Button>
                                            
                                            <p className="text-xs text-slate-500 text-center">
                                                Un email sera envoyé au client avec le bon de SAV 
                                                {savDetails.sav.return_label_url ? ' et le bon de transport' : ''} en pièce jointe.
                                                La décote sera appliquée après réception et contrôle des appareils.
                                            </p>
                                            
                                            <Button
                                                variant="destructive"
                                                onClick={() => handleUpdateStatus('rejected')}
                                                disabled={actionLoading}
                                                className="w-full"
                                            >
                                                <X className="w-4 h-4 mr-2" />
                                                Refuser tout le SAV
                                            </Button>
                                        </div>
                                    )}

                                    {/* Approved/Partial -> Mark as received */}
                                    {['approved', 'partial', 'shipped_back'].includes(savDetails.sav.status) && (
                                        <Button
                                            onClick={() => handleUpdateStatus('received')}
                                            disabled={actionLoading}
                                        >
                                            <Package className="w-4 h-4 mr-2" />
                                            Marquer comme reçu
                                        </Button>
                                    )}

                                    {/* Received -> Processing */}
                                    {savDetails.sav.status === 'received' && (
                                        <Button
                                            onClick={() => handleUpdateStatus('processing')}
                                            disabled={actionLoading}
                                        >
                                            <RefreshCw className="w-4 h-4 mr-2" />
                                            Commencer l'examen
                                        </Button>
                                    )}

                                    {/* Processing -> Refund (credit or transfer) or Reject */}
                                    {savDetails.sav.status === 'processing' && (
                                        <div className="space-y-3">
                                            {/* Summary of validation changes */}
                                            <div className="p-3 bg-slate-50 rounded border">
                                                <p className="text-sm font-medium mb-1">État des validations:</p>
                                                <p className="text-sm text-green-600">
                                                    ✓ {Object.values(itemValidations).filter(v => v.status === 'approved').length} appareil(s) accepté(s)
                                                </p>
                                                <p className="text-sm text-red-600">
                                                    ✗ {Object.values(itemValidations).filter(v => v.status === 'rejected').length} appareil(s) refusé(s)
                                                </p>
                                            </div>
                                            
                                            {/* Summary of approved items with discounts */}
                                            <div className="p-3 bg-green-50 rounded border border-green-200">
                                                <p className="text-sm font-medium mb-2 text-green-800">Récapitulatif crédit:</p>
                                                {savDetails.sav.items?.length > 0 ? (
                                                    <div className="space-y-1">
                                                        {savDetails.sav.items.filter(item => 
                                                            itemValidations[item.imei]?.status === 'approved'
                                                        ).map(item => {
                                                            const discount = itemDiscounts[item.imei] || 0;
                                                            const finalPrice = (item.item?.price || 0) - discount;
                                                            return (
                                                                <div key={item.imei} className="flex justify-between text-sm">
                                                                    <div className="flex items-center gap-2">
                                                                        <span>{item.item?.brand} {item.item?.model}</span>
                                                                        {/* Discount input for processing phase */}
                                                                        <div className="flex items-center gap-1">
                                                                            <span className="text-xs text-slate-400">Décote:</span>
                                                                            <input
                                                                                type="number"
                                                                                value={itemDiscounts[item.imei] || ''}
                                                                                onChange={(e) => setItemDiscount(item.imei, e.target.value)}
                                                                                className="w-16 px-1 py-0.5 text-xs border rounded"
                                                                                placeholder="0"
                                                                                step="0.01"
                                                                                min="0"
                                                                            />
                                                                            <span className="text-xs text-slate-400">€</span>
                                                                        </div>
                                                                    </div>
                                                                    <span className="font-medium">
                                                                        {discount > 0 ? (
                                                                            <>
                                                                                <span className="line-through text-slate-400 mr-2">{item.item?.price?.toFixed(2)}€</span>
                                                                                <span className="text-green-700">{finalPrice.toFixed(2)}€</span>
                                                                            </>
                                                                        ) : (
                                                                            `${item.item?.price?.toFixed(2)}€`
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                        {Object.values(itemValidations).filter(v => v.status === 'approved').length === 0 && (
                                                            <p className="text-sm text-slate-500 italic">Aucun appareil accepté</p>
                                                        )}
                                                    </div>
                                                ) : savDetails.sav.imei && (
                                                    <div className="flex justify-between text-sm">
                                                        <span>{savDetails.sav.item?.brand} {savDetails.sav.item?.model}</span>
                                                        <span className="font-medium">{savDetails.sav.item?.price?.toFixed(2)}€</span>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium mb-2">Montant du crédit/avoir</label>
                                                <Input
                                                    type="number"
                                                    value={creditAmount}
                                                    onChange={(e) => setCreditAmount(e.target.value)}
                                                    placeholder="Montant TTC"
                                                    step="0.01"
                                                />
                                                <p className="text-xs text-slate-500 mt-1">
                                                    Ce montant sera crédité au wallet du client et généré comme avoir Pennylane
                                                </p>
                                            </div>
                                            <div className="flex gap-3">
                                                <Button
                                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                                    onClick={() => handleUpdateStatus('refunded_credit', 'credit')}
                                                    disabled={actionLoading}
                                                >
                                                    <CreditCard className="w-4 h-4 mr-2" />
                                                    Crédit boutique
                                                </Button>
                                                <Button
                                                    className="flex-1"
                                                    variant="outline"
                                                    onClick={() => handleUpdateStatus('refunded_transfer', 'transfer')}
                                                    disabled={actionLoading}
                                                >
                                                    <Banknote className="w-4 h-4 mr-2" />
                                                    Virement
                                                </Button>
                                            </div>
                                            <Button
                                                variant="destructive"
                                                onClick={() => handleUpdateStatus('rejected')}
                                                disabled={actionLoading}
                                            >
                                                <X className="w-4 h-4 mr-2" />
                                                Refuser (hors garantie)
                                            </Button>
                                        </div>
                                    )}

                                    {/* Show credit note info if completed */}
                                    {['refunded_credit', 'refunded_transfer'].includes(savDetails.sav.status) && savDetails.sav.credit_note_number && (
                                        <div className="p-4 bg-green-50 rounded-lg">
                                            <p className="font-medium text-green-800">
                                                Avoir créé: {savDetails.sav.credit_note_number}
                                            </p>
                                            {savDetails.sav.credit_amount && (
                                                <p className="text-green-700">
                                                    Crédit: {savDetails.sav.credit_amount.toFixed(2)} €
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setSelectedSAV(null)}>
                                Fermer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default AdminSAVPage;
